import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import api from '../api/client';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../contexts/PermissionContext';
import { signupEvent } from './whatsappSignup.mjs';

type Session = { state: string; appId: string; configId: string; graphVersion: string };
type Connection = { phone_number_id: string; display_phone_number: string; status: string; is_active: boolean };
type MetaSdk = { init(options: object): void; login(callback: (response: {authResponse?: {code?: string}}) => void, options: object): void };
declare global { interface Window { FB?: MetaSdk } }

function Panel({organizationId,workspaceId}: {organizationId: string; workspaceId: string}) {
  const requestConfig={sparkScope:{organizationId,workspaceId}};
  const [connections, setConnections] = useState<Connection[]>([]);
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reconnect, setReconnect] = useState(false);
  const [pin, setPin] = useState('');
  const alive = useRef(true);
  const cleanup = useRef<() => void>(() => {});
  const refresh = async () => { if(!alive.current) return; const { data } = await api.get('/api/v1/whatsapp/connections',requestConfig); if (alive.current) setConnections(data); };
  useEffect(() => { alive.current = true; void refresh().catch(() => { if(alive.current) setError('Connections could not be loaded. Check workspace permissions and try again.'); }); return () => { alive.current = false; cleanup.current(); }; }, []);
  const action = async (phone: string, operation: string) => {
    setBusy(true); setError('');
    try { await api.post(`/api/v1/whatsapp/connections/${encodeURIComponent(phone)}/${operation}`,operation==='register'?{pin}:undefined,requestConfig); await refresh(); }
    catch { if(alive.current) setError('Action not completed. Verify Meta registration, app subscription and permissions. Refresh before retrying.'); }
    finally { if(alive.current) { setBusy(false); setPin(''); } }
  };
  const prepare = async (isReconnect: boolean) => {
    setBusy(true); setError(''); setSession(null); setReconnect(isReconnect);
    try {
      const { data } = await api.post<Session>('/api/v1/whatsapp/signup/sessions',undefined,requestConfig);
      if(!alive.current) return;
      if (!window.FB) await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script'); script.src = 'https://connect.facebook.net/en_US/sdk.js'; script.async = true;
        const timer = window.setTimeout(() => { script.remove(); reject(new Error('SDK unavailable')); }, 15000);
        script.onload = () => { clearTimeout(timer); resolve(); }; script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('SDK unavailable')); }; document.head.appendChild(script);
      });
      if(!alive.current) return;
      if(!window.FB) throw new Error('SDK unavailable');
      window.FB.init({appId: data.appId, version: data.graphVersion, cookie: false, xfbml: false}); setSession(data);
    } catch { if(alive.current) setError('Signup is unavailable. Check administrator configuration, entitlement and browser access to Meta.'); }
    finally { if(alive.current) setBusy(false); }
  };
  const launch = () => {
    if(!session || !window.FB) return;
    setBusy(true); setError('');
    let code: string | undefined; let assets: {wabaId: string; phoneId: string} | null = null; let finished = false;
    const finish = async () => {
      if(finished || !code || !assets || !alive.current) return;
      finished = true; cleanup.current();
      try { await api.post('/api/v1/whatsapp/signup/complete', {state: session.state, code, ...assets, reconnect},requestConfig); if(alive.current) { setSession(null); await refresh(); } }
      catch { if(alive.current) setError('Connection could not be verified. Begin a new signup; existing connections have not been transferred.'); }
      finally { if(alive.current) setBusy(false); }
    };
    const listener = (event: MessageEvent) => { const result = signupEvent(event.origin, event.data); if(result) { assets = result; void finish(); } };
    window.addEventListener('message', listener);
    const timer = window.setTimeout(() => { finished = true; cleanup.current(); if(alive.current) { setBusy(false); setSession(null); setError('Signup expired or was cancelled. Start again.'); } }, 120000);
    cleanup.current = () => { window.removeEventListener('message', listener); clearTimeout(timer); };
    try {
      window.FB.login(response => {
        code = response.authResponse?.code;
        if(!code) { finished=true; cleanup.current(); if(alive.current) { setBusy(false); setSession(null); setError('Meta authorization was cancelled. Start again.'); } return; }
        void finish();
      }, {
        config_id: session.configId, response_type: 'code', override_default_response_type: true,
        extras: {setup: {}, sessionInfoVersion: '3'}
      });
    } catch { finished=true; cleanup.current(); setBusy(false); setSession(null); setError('Meta signup could not open. Allow the popup and try again.'); }
  };
  return <Paper variant="outlined" sx={{p:2, mb:3}}><Stack spacing={1}>
    <Typography variant="h6">WhatsApp connection</Typography>
    <Typography>Connect your business through Meta. No marketing messages are sent during setup.</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    <Button disabled={busy} onClick={() => void prepare(false)}>Connect WhatsApp</Button>
    {session && <Button disabled={busy} onClick={launch}>Continue with Meta</Button>}
    <Button disabled={busy} onClick={() => void refresh().catch(() => setError('Refresh failed.'))}>Refresh status</Button>
    {connections.map(c => <Stack key={c.phone_number_id} spacing={1}>
      <Typography>{c.display_phone_number} — {c.is_active ? c.status : `${c.status} · sending disabled`}</Typography>
      {c.status !== 'DISCONNECTED' && <>
        <Typography variant="body2">Complete phone registration and the Spark CRM app subscription in Meta, then verify activation.</Typography>
        {!c.is_active && <>
          <TextField type="password" autoComplete="off" label="Six-digit Meta registration PIN" value={pin} onChange={e=>setPin(e.target.value)} inputProps={{maxLength:6,inputMode:'numeric'}} />
          <Button disabled={busy || !/^\d{6}$/.test(pin)} onClick={()=>void action(c.phone_number_id,'register')}>Register Cloud API phone and subscribe app</Button>
          <Typography variant="caption">For standard Cloud API onboarding only. Coexistence or migration flows require Meta-specific setup. The PIN is not stored.</Typography>
        </>}
        <Button disabled={busy} onClick={() => void action(c.phone_number_id,'activate')}>Verify activation</Button>
        <Button disabled={busy} onClick={() => void action(c.phone_number_id,'disconnect')}>Disconnect from Spark CRM</Button>
      </>}
      {c.status === 'DISCONNECTED' && <Button disabled={busy} onClick={() => void prepare(true)}>Reconnect the same account</Button>}
    </Stack>)}
  </Stack></Paper>;
}
export default function WhatsAppConnections() {
  const tenant=useTenant(); const permissions=usePermissions();
  if(tenant.loading || permissions.loading || !tenant.organizationId || !tenant.workspaceId || !permissions.hasPermission('COMM_WABA_MANAGE')) return null;
  return <Panel key={JSON.stringify([tenant.organizationId,tenant.workspaceId])} organizationId={tenant.organizationId} workspaceId={tenant.workspaceId} />;
}
